import { getApiUrl } from './api';
import { apiFetch } from './apiFetch';

export const LEGACY_OPERATIONAL_ALERT_QUEUE_KEY = 'brutos-operational-alert-queue-v1';
export const OPERATIONAL_ALERT_QUEUE_KEY_PREFIX = 'brutos-operational-alert-queue-v2';
export const OPERATIONAL_ALERT_QUEUE_LIMIT = 30;
export const OPERATIONAL_ALERT_DEDUPE_MS = 2 * 60 * 1000;
export const OPERATIONAL_ALERT_TTL_MS = 18 * 60 * 60 * 1000;
export const OPERATIONAL_ALERT_REPEAT_LIMIT = 1000;

const OPERATIONAL_ALERT_ENDPOINT = `${getApiUrl()}/api/system/operational-alerts`;
const operationalAlertFlushPromises = new Map();

const normalizeTenantKey = (value) => String(value || '').trim();

const resolveStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const getTimestamp = (value, fallback = 0) => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : fallback;
};

export const getOperationalAlertQueueKey = (tenantKey) => {
  const normalizedTenantKey = normalizeTenantKey(tenantKey);
  return normalizedTenantKey
    ? `${OPERATIONAL_ALERT_QUEUE_KEY_PREFIX}:${encodeURIComponent(normalizedTenantKey)}`
    : '';
};

export const discardLegacyOperationalAlertQueue = (storage) => {
  const resolvedStorage = resolveStorage(storage);
  if (!resolvedStorage) return false;

  try {
    const hadLegacyQueue = resolvedStorage.getItem(LEGACY_OPERATIONAL_ALERT_QUEUE_KEY) !== null;
    resolvedStorage.removeItem(LEGACY_OPERATIONAL_ALERT_QUEUE_KEY);
    return hadLegacyQueue;
  } catch {
    return false;
  }
};

const keepTenantAlert = (item, tenantKey, sessionKey = '') => (
  item
  && typeof item === 'object'
  && normalizeTenantKey(item.tenantKey) === tenantKey
  && (!sessionKey || String(item.sessionKey || '') === sessionKey)
);

export const readOperationalAlertQueue = (tenantKey, { storage, sessionKey = '' } = {}) => {
  const normalizedTenantKey = normalizeTenantKey(tenantKey);
  const normalizedSessionKey = String(sessionKey || '').trim();
  const queueKey = getOperationalAlertQueueKey(normalizedTenantKey);
  const resolvedStorage = resolveStorage(storage);
  if (!queueKey || !resolvedStorage) return [];

  discardLegacyOperationalAlertQueue(resolvedStorage);

  try {
    const parsed = JSON.parse(resolvedStorage.getItem(queueKey) || '[]');
    if (!Array.isArray(parsed)) return [];

    const isolatedQueue = parsed
      .filter((item) => keepTenantAlert(item, normalizedTenantKey, normalizedSessionKey))
      .slice(0, OPERATIONAL_ALERT_QUEUE_LIMIT);

    if (isolatedQueue.length !== parsed.length) {
      resolvedStorage.setItem(queueKey, JSON.stringify(isolatedQueue));
    }
    return isolatedQueue;
  } catch {
    try {
      resolvedStorage.removeItem(queueKey);
    } catch {
      // A fila permanece inutilizavel, mas nunca e atribuida ao tenant atual.
    }
    return [];
  }
};

export const writeOperationalAlertQueue = (tenantKey, queue = [], { storage, sessionKey = '' } = {}) => {
  const normalizedTenantKey = normalizeTenantKey(tenantKey);
  const normalizedSessionKey = String(sessionKey || '').trim();
  const queueKey = getOperationalAlertQueueKey(normalizedTenantKey);
  const resolvedStorage = resolveStorage(storage);
  if (!queueKey || !resolvedStorage) return false;

  discardLegacyOperationalAlertQueue(resolvedStorage);

  const isolatedQueue = (Array.isArray(queue) ? queue : [])
    .filter((item) => keepTenantAlert(item, normalizedTenantKey, normalizedSessionKey))
    .slice(0, OPERATIONAL_ALERT_QUEUE_LIMIT);

  try {
    resolvedStorage.setItem(queueKey, JSON.stringify(isolatedQueue));
    return true;
  } catch {
    return false;
  }
};

export const getOperationalAlertPath = (url = '', windowRef = typeof window !== 'undefined' ? window : null) => {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) return '';

  try {
    const origin = windowRef?.location?.origin || 'https://gestor.brutosdelivery.cloud';
    return new URL(rawUrl, origin).pathname.slice(0, 180);
  } catch {
    return rawUrl
      .replace(/^https?:\/\/[^/]+/i, '')
      .split(/[?#]/, 1)[0]
      .slice(0, 180);
  }
};

const buildBrowserContext = (windowRef, navigatorRef) => {
  if (!windowRef) return {};
  return {
    location: getOperationalAlertPath(windowRef.location?.pathname || '/', windowRef),
    online: navigatorRef ? navigatorRef.onLine : null,
    userAgent: navigatorRef?.userAgent || '',
  };
};

const normalizeAlertDetails = (details, windowRef, navigatorRef) => {
  const rawDetails = details && typeof details === 'object' ? details : {};
  const {
    endpoint,
    location,
    path,
    url,
    ...remainingDetails
  } = rawDetails;

  const browserContext = buildBrowserContext(windowRef, navigatorRef);
  return {
    ...browserContext,
    ...remainingDetails,
    ...(location || browserContext.location
      ? { location: getOperationalAlertPath(location || browserContext.location, windowRef) }
      : null),
    path: getOperationalAlertPath(path || endpoint || url || '', windowRef),
  };
};

export const buildOperationalAlertFingerprint = (alert = {}) => [
  normalizeTenantKey(alert.tenantKey),
  alert.sessionKey || '',
  alert.type || '',
  alert.source || '',
  alert.message || '',
  alert.details?.channel || '',
  alert.details?.path || '',
].join('|').slice(0, 500);

const pruneOperationalAlertQueue = (queue, now) => queue.filter((item) => {
  const lastSeenAt = getTimestamp(item?.lastSeenAt, getTimestamp(item?.queuedAt));
  return lastSeenAt > 0 && now - lastSeenAt <= OPERATIONAL_ALERT_TTL_MS;
});

export const enqueueOperationalAlert = (tenantKey, alert = {}, options = {}) => {
  const normalizedTenantKey = normalizeTenantKey(tenantKey);
  if (!normalizedTenantKey) return null;
  if (options.isTenantCurrent && !options.isTenantCurrent(normalizedTenantKey)) return null;

  const now = getTimestamp(options.now, Date.now());
  const windowRef = options.windowRef === undefined
    ? (typeof window !== 'undefined' ? window : null)
    : options.windowRef;
  const navigatorRef = options.navigatorRef === undefined
    ? (typeof navigator !== 'undefined' ? navigator : null)
    : options.navigatorRef;
  const entry = {
    tenantKey: normalizedTenantKey,
    ...(options.sessionKey ? { sessionKey: String(options.sessionKey) } : null),
    queuedAt: now,
    firstSeenAt: now,
    lastSeenAt: now,
    repeatCount: 1,
    severity: alert.severity || 'warning',
    type: alert.type || 'frontend.operational',
    source: alert.source || 'Pedidos',
    message: alert.message || 'Falha operacional no frontend.',
    details: normalizeAlertDetails(alert.details, windowRef, navigatorRef),
  };
  const fingerprint = buildOperationalAlertFingerprint(entry);
  const queue = pruneOperationalAlertQueue(
    readOperationalAlertQueue(normalizedTenantKey, options),
    now
  );
  const duplicateIndex = queue.findIndex((item) => (
    item.fingerprint === fingerprint
    && now - getTimestamp(item.lastSeenAt, getTimestamp(item.queuedAt)) <= OPERATIONAL_ALERT_DEDUPE_MS
  ));

  let queuedEntry;
  if (duplicateIndex >= 0) {
    const previous = queue[duplicateIndex];
    queuedEntry = {
      ...previous,
      tenantKey: normalizedTenantKey,
      queuedAt: getTimestamp(previous.queuedAt, now),
      firstSeenAt: getTimestamp(previous.firstSeenAt, getTimestamp(previous.queuedAt, now)),
      lastSeenAt: now,
      repeatCount: Math.min(
        OPERATIONAL_ALERT_REPEAT_LIMIT,
        Math.max(1, Number(previous.repeatCount) || 1) + 1
      ),
      severity: entry.severity,
      details: entry.details,
      fingerprint,
    };
    queue[duplicateIndex] = queuedEntry;
  } else {
    queuedEntry = { ...entry, fingerprint };
    queue.unshift(queuedEntry);
  }

  writeOperationalAlertQueue(normalizedTenantKey, queue, options);
  return queuedEntry;
};

const readResponseBody = async (response) => {
  if (typeof response?.json !== 'function') return null;
  return response.json().catch(() => null);
};

const getQueueItemIdentity = (item) => JSON.stringify([
  item?.tenantKey || '',
  item?.sessionKey || '',
  item?.fingerprint || '',
  item?.queuedAt || 0,
  item?.lastSeenAt || 0,
  item?.repeatCount || 0,
]);

export const flushOperationalAlertQueue = async (tenantKey, options = {}) => {
  const normalizedTenantKey = normalizeTenantKey(tenantKey);
  if (!normalizedTenantKey) return { attempted: 0, delivered: 0, remaining: 0 };

  const flushKey = `${normalizedTenantKey}\u0000${String(options.sessionKey || '')}`;
  const existingFlush = operationalAlertFlushPromises.get(flushKey);
  if (existingFlush) return existingFlush;

  const flushPromise = (async () => {
    const isTenantCurrent = options.isTenantCurrent || (() => true);
    if (!isTenantCurrent(normalizedTenantKey)) {
      return { attempted: 0, delivered: 0, remaining: 0 };
    }

    const now = getTimestamp(options.now, Date.now());
    const queue = pruneOperationalAlertQueue(
      readOperationalAlertQueue(normalizedTenantKey, options),
      now
    );
    const request = options.request || apiFetch;
    const endpoint = options.endpoint || OPERATIONAL_ALERT_ENDPOINT;
    const deliveredItems = new Set();
    let attempted = 0;

    for (const alert of queue) {
      if (!isTenantCurrent(normalizedTenantKey)) break;
      attempted += 1;
      try {
        const payload = { ...alert };
        delete payload.sessionKey;
        const response = await request(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const body = await readResponseBody(response);
        if (response?.ok && body?.ok === true) {
          deliveredItems.add(getQueueItemIdentity(alert));
        }
      } catch {
        // Falhas tecnicas permanecem na fila para a proxima tentativa.
      }
    }

    if (!isTenantCurrent(normalizedTenantKey)) {
      return {
        attempted,
        delivered: deliveredItems.size,
        remaining: Math.max(0, queue.length - deliveredItems.size),
      };
    }

    const latestQueue = pruneOperationalAlertQueue(
      readOperationalAlertQueue(normalizedTenantKey, options),
      getTimestamp(options.now, Date.now())
    );
    const remainingQueue = latestQueue.filter(
      (item) => !deliveredItems.has(getQueueItemIdentity(item))
    );
    writeOperationalAlertQueue(normalizedTenantKey, remainingQueue, options);

    return {
      attempted,
      delivered: deliveredItems.size,
      remaining: remainingQueue.length,
    };
  })().finally(() => {
    operationalAlertFlushPromises.delete(flushKey);
  });

  operationalAlertFlushPromises.set(flushKey, flushPromise);
  return flushPromise;
};

const normalizeAuthFailureText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export const isOperationalAlertAuthFailure = (candidate = {}) => {
  const status = Number(candidate.status || candidate.response?.status || 0);
  const body = candidate.responseBody || candidate.body || candidate.payload || {};
  const code = String(body?.code || candidate.code || '').trim().toUpperCase();
  const message = normalizeAuthFailureText(
    body?.error || body?.message || candidate.message || ''
  );

  if (status === 401) return true;
  if (status === 403) {
    return code === 'PAYMENT_PENDING'
      || code === 'MODULE_ACCESS_DISABLED'
      || body?.paymentPending === true
      || body?.moduleAccessDisabled === true
      || message.includes('pendente de pagamento')
      || message.includes('modulo nao esta liberado');
  }
  return status === 400 && message.includes('token invalido');
};

export const reportOperationalAlert = (tenantKey, alert = {}, options = {}) => {
  const queued = enqueueOperationalAlert(tenantKey, alert, options);
  if (!queued) return null;

  console.warn('[OperationalAlert]', queued);
  flushOperationalAlertQueue(tenantKey, options).catch(() => {});
  return queued;
};
